import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DefaultLayout from './DefaultLayout';
import { useParams } from 'react-router-dom';
import { getAllCars } from '../redux/actions/carsActions';
import Spinner from '../components/Spinner';
import { Col, Row, Divider, DatePicker, Space, Modal, Input, message } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import PlacesAutocomplete from 'react-places-autocomplete';

const { RangePicker } = DatePicker;
const disabledDate = current => current && current < dayjs().startOf('day');

function getLoggedInUser() {
  const data = JSON.parse(localStorage.getItem('user'));
  return data?.user || null;
}

function Booking() {
  const { carid } = useParams();
  const { cars } = useSelector(state => state.cars);
  const { loading } = useSelector(state => state.alertsReducer);
  const dispatch = useDispatch();

  const [car, setCar] = useState(null);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [totalHours, setTotalHours] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [displayRange, setDisplayRange] = useState('');

  const [pickup, setPickup] = useState('');
  const [destination, setDestination] = useState('');
  const [distanceInMiles, setDistanceInMiles] = useState(0);
  const [duration, setDuration] = useState('');

  const averageSpeed = 30;
  const estimatedTime = distanceInMiles / averageSpeed;
  const estimatedTimeInMinutes = Math.round(estimatedTime * 60);
  const estimatedTimeFormatted = estimatedTimeInMinutes > 0
    ? `${Math.floor(estimatedTimeInMinutes / 60)} hours ${estimatedTimeInMinutes % 60} minutes`
    : 'Less than 1 minute';

  useEffect(() => {
    if (cars.length === 0) {
      dispatch(getAllCars());
    } else {
      const foundCar = cars.find(o => o._id === carid);
      setCar(foundCar || null);
    }
  }, [cars, carid, dispatch]);

  useEffect(() => {
    async function updatePricing() {
      if (pickup && destination) {
        try {
          const response = await axios.post('https://luxurylifelimo.onrender.com/api/bookings/calculate-price', {
            pickup,
            destination
          });

          const { distanceInMiles, price, duration } = response.data;
          setDistanceInMiles(distanceInMiles);
          setTotalAmount(price);
          setDuration(duration);
        } catch (err) {
          console.error('Error calculating distance:', err);
          message.error('Error calculating distance. Please check your addresses.');
        }
      }
    }

    updatePricing();
  }, [pickup, destination]);

  function selectTimeSlots(values) {
    if (values?.length === 2) {
      const [start, end] = values;
      setFrom(dayjs(start).toISOString());
      setTo(dayjs(end).toISOString());

      const formattedStart = dayjs(start).format('MMM DD YYYY HH:mm');
      const formattedEnd = dayjs(end).format('MMM DD YYYY HH:mm');
      setDisplayRange(`${formattedStart} - ${formattedEnd}`);

      const hours = end.diff(start, 'minutes') / 60;
      setTotalHours(hours.toFixed(2));
    }
  }

  async function handleCheckout() {
    const user = getLoggedInUser();

    if (!user?._id || !car?._id) {
      message.error("Please log in and ensure car is loaded before booking.");
      return;
    }

    try {
      const response = await fetch('https://luxurylifelimo.onrender.com/api/bookings/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user._id,
          car: car._id,
          pickupLocation: pickup,
          dropoffLocation: destination,
          distanceInMiles,
          estimatedTime: estimatedTimeInMinutes,
          totalAmount,
          bookedTimeSlots: [{ from, to }]
        })
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        message.error('Failed to initiate payment.');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      message.error('Something went wrong. Please try again later.');
    }
  }

  return (
    <DefaultLayout>
      {loading && <Spinner />}

      {!loading && !car && (
        <div style={{ textAlign: 'center', marginTop: '100px' }}>
          <h3>Car not found.</h3>
        </div>
      )}

      {!loading && car && (
        <>
          <Row justify="center" style={{ minHeight: '90vh' }} className="d-flex align-items-center">
            <Col lg={10} sm={24} xs={24}>
              <img src={car.image} alt={car.make} className="carimg2 bs1 w-100 p-2" />
            </Col>

            <Col lg={10} sm={24} xs={24} className="text-right">
              <Divider dashed style={{ borderColor: 'black' }}>Vehicle Info</Divider>
              <div>
                <strong><p>{car.make}</p></strong>
                <p>${car.rentPerHour} Rate Per 25 Miles /-</p>
                <p>Fuel Type: {car.fuelType}</p>
                <p>Max Persons: {car.capacity}</p>
              </div>

              <Divider dashed style={{ borderColor: 'black' }}>Enter Trip Addresses</Divider>

              <PlacesAutocomplete value={pickup} onChange={setPickup} onSelect={setPickup}>
                {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                  <>
                    <Input {...getInputProps({ placeholder: 'Pickup Address', className: 'mb-2' })} />
                    <div>
                      {suggestions.map(suggestion => (
                        <div {...getSuggestionItemProps(suggestion)} key={suggestion.placeId}>
                          {suggestion.description}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </PlacesAutocomplete>

              <PlacesAutocomplete value={destination} onChange={setDestination} onSelect={setDestination}>
                {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                  <>
                    <Input {...getInputProps({ placeholder: 'Dropoff Address', className: 'mb-3' })} />
                    <div>
                      {suggestions.map(suggestion => (
                        <div {...getSuggestionItemProps(suggestion)} key={suggestion.placeId}>
                          {suggestion.description}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </PlacesAutocomplete>

              <Divider dashed style={{ borderColor: 'black' }}>Select Time Slot</Divider>

              <Space direction="vertical" size={12}>
                <RangePicker
                  onChange={selectTimeSlots}
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  disabledDate={disabledDate}
                  style={{ width: '100%' }}
                />
              </Space>

              <button className="btn1 mt-2" onClick={() => setShowModal(true)}>See Booked Slots</button>

              {from && to && (
                <div style={{ marginTop: 20 }}>
                  {displayRange && <p><b>{displayRange}</b></p>}
                  {distanceInMiles > 0 && (
                    <>
                      <p>Distance: <b>{distanceInMiles.toFixed(2)} miles</b></p>
                      <p>Estimated Time: <b>{estimatedTimeFormatted}</b></p>
                    </>
                  )}
                  <h3>Total Amount:$ {totalAmount}</h3>

                  <button
                    className="btn1"
                    onClick={handleCheckout}
                    disabled={!pickup || !destination || !from || !to || totalAmount <= 0}
                  >
                    Book Now
                  </button>
                </div>
              )}
            </Col>
          </Row>

          <Modal open={showModal} footer={false} closable={false} title="Booked time slots">
            <div className="p-2">
              {car.bookedTimeSlots?.length > 0 ? (
                car.bookedTimeSlots.map((slot, index) => (
                  <button className="btn1 mt-2" key={index}>
                    {slot.from} - {slot.to}
                  </button>
                ))
              ) : (
                <p>No booked slots yet.</p>
              )}
              <div className="text-right mt-5">
                <button onClick={() => setShowModal(false)} className="btn1">CLOSE</button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </DefaultLayout>
  );
}

export default Booking;
